import React, { useMemo, useState } from "react";
import { PICO_COLORS } from "../../../../utils/Picocolors";

const TagFilterPanel = ({ tagOptions = [], selectedTagIds = [], onToggleTag, onClearAll, tagsHeight = "vh-20" }) => {
    const [showAllTags, setShowAllTags] = useState(false);

    const normalizedTags = useMemo(() => {
        const input = Array.isArray(tagOptions) ? tagOptions : [];

        return input
            .map((t) => {
                const text = t?.tag || t?.tags || t?.name || t?.value || "";
                const rawCat = String(
                    t?.picos_category || t?.pico_category || t?.category || t?.picosCategory || "",
                ).toLowerCase();

                let category = rawCat;
                if (category.startsWith("pop")) category = "population";
                if (category.startsWith("int")) category = "intervention";
                if (category.startsWith("comp")) category = "comparison";
                if (category.startsWith("out")) category = "outcome";
                if (category === "study design") category = "study_design";
                if (category.startsWith("study")) category = "study_design";
                if (category.startsWith("exclu")) category = "exclusion";
                if (!category) category = "other";
                if (!PICO_COLORS[category]) category = "other";

                const subcategory = t?.subcategory || "Unclassified";

                return { id: t?.id, text, category, subcategory };
            })
            .filter((t) => t?.id && t?.text);
    }, [tagOptions]);

    const visibleTagsGrouped = useMemo(() => {
        const tagsToGroup = showAllTags ? normalizedTags : normalizedTags.slice(0, 10);
        
        // Output format: { subcategoryName: [tags] }
        const groups = {};
        tagsToGroup.forEach(t => {
            if (!groups[t.subcategory]) {
                groups[t.subcategory] = [];
            }
            groups[t.subcategory].push(t);
        });

        return groups;
    }, [showAllTags, normalizedTags]);

    const [expandedSubs, setExpandedSubs] = useState({});

    const handleExpandToggle = (sub) => {
        setExpandedSubs(prev => ({ ...prev, [sub]: !prev[sub] }));
    };

    return (
        <div>

            <div className="ev-card-8 p-1 ev-radius-xsmall mb-2">
                <div className="row gx-2 gy-1">
                    {Object.entries(PICO_COLORS || {}).map(([key, color]) => (
                        <div key={key} className="col-4">
                            <div className="d-flex align-items-center gap-1">
                                <span
                                    style={{
                                        width: 8,
                                        height: 8,
                                        backgroundColor: color,
                                        borderRadius: 2,
                                        flexShrink: 0,
                                    }}
                                />
                                <span className="ev-xxsmall-p-blue text-truncate">
                                    {key === "comparison"
                                        ? "Comparator"
                                        : key
                                        .replace(/_/g, " ")
                                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className={`d-flex flex-wrap gap-1 align-items-start align-content-start vh-1 scrollable-element pt-1 ${tagsHeight}`}>
                {Object.keys(visibleTagsGrouped).length > 0 ? (
                    Object.entries(visibleTagsGrouped).map(([sub, subTags]) => {
                        const firstColor = PICO_COLORS[subTags[0]?.category] || PICO_COLORS.population;
                        const allSelected = subTags.every(t => selectedTagIds?.includes(t.id));

                        return (
                            <React.Fragment key={sub}>
                                {/* Split Pill Button */}
                                <div className="d-flex align-items-stretch overflow-hidden ev-btn-xsmall ev-bold-reg ev-radius-xsmall" style={{ 
                                    background: firstColor ? `${firstColor}45` : "#e2e4ee",
                                    border: allSelected ? `1.5px solid ${firstColor}` : "1.5px solid transparent",
                                    color: firstColor,
                                }}>
                                    {/* Left side: select subcategory items */}
                                    <button
                                        type="button"
                                        className="border-0 bg-transparent text-nowrap ps-2 pe-0 ev-bold-reg"
                                        onClick={() => {
                                            subTags.forEach(t => {
                                                if (allSelected) {
                                                    if (selectedTagIds.includes(t.id)) onToggleTag(t.id);
                                                } else {
                                                    if (!selectedTagIds.includes(t.id)) onToggleTag(t.id);
                                                }
                                            });
                                        }}
                                        style={{ color: firstColor }}
                                    >
                                        {sub}
                                    </button>

                                    {/* Right side: dropdown toggle */}
                                    <div 
                                        className="d-flex align-items-center justify-content-center px-2 cursor-pointer"
                                        style={{ color: firstColor }}
                                        onClick={() => handleExpandToggle(sub)}
                                    >
                                        <i className={`bi ${expandedSubs[sub] ? "bi-chevron-up" : "bi-chevron-down"}`} style={{ fontSize: "10px" }} />
                                    </div>
                                </div>
                                
                                {/* Tags */}
                                {expandedSubs[sub] && subTags.map((t) => {
                        const color = PICO_COLORS[t.category] || PICO_COLORS.population;
                        const isSel = selectedTagIds?.includes(t.id);

                        return (
                            <button
                                key={t.id}
                                type="button"
                                className={`ev-btn-xsmall ev-bold-reg py-1 px-2 ev-radius-xsmall ${isSel ? "d-flex align-items-center gap-1" : ""}`}
                                onClick={() => onToggleTag(t.id)}
                                style={{
                                    background: color ? `${color}${isSel ? "33" : "15"}` : "#f2f3f8",
                                    color: color,
                                    border: isSel ? `1.5px solid ${color}` : "1.5px solid transparent",
                                }}
                            >
                                {t.text}
                                {isSel && <i className="bi bi-x-lg"></i>}
                            </button>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })
                ) : (
                    <span className="ev-xsmall-p-dark">No tags configured yet.</span>
                )}

                <div className="d-flex justify-content-between align-items-center w-100 mt-1 mb-2 px-1">
                {normalizedTags.length > 12 && (
                    <button
                        type="button"
                            className="ev-btn-text-only-fill ev-btn-xsmall p-0"
                        onClick={() => setShowAllTags((p) => !p)}
                    >
                        {showAllTags ? "See Less Tags" : "See More Tags"}
                    </button>
                )}
                    {onClearAll && selectedTagIds?.length > 0 ? (
                        <button
                            type="button"
                            className="ev-btn-text-only-fill ev-btn-xsmall p-0"
                            onClick={onClearAll}
                        >
                            Clear tags
                        </button>
                    ) : (
                        <span />
                    )}
                </div>
            </div>
        </div>
    );
};

export default TagFilterPanel;